import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, CircleAlert, Info, Loader2, LockKeyhole, MailCheck } from 'lucide-react';
import api, { translateApiError } from '../api/client';
import ditaLogo from '../icons.png';
import flagMd from '../flag-md.png';

const THERAPEUTIC_AREAS = [
  { value: 'cardiology', label: 'Cardiologie' },
  { value: 'traumatology_orthopedics', label: 'Traumatologie / Ortopedie' },
  { value: 'neurology', label: 'Neurologie' },
  { value: 'endocrinology_diabetology', label: 'Endocrinologie / Diabetologie' },
  { value: 'family_medicine', label: 'Medicină de familie' },
  { value: 'gynecology_obstetrics', label: 'Ginecologie / Obstetrică' },
  { value: 'allergology_immunology', label: 'Alergologie / Imunologie' },
  { value: 'gastroenterology', label: 'Gastroenterologie' },
  { value: 'pneumology', label: 'Pneumologie' },
  { value: 'orl', label: 'ORL' },
  { value: 'ophthalmology', label: 'Oftalmologie' },
  { value: 'dermatology', label: 'Dermatologie' },
  { value: 'urology', label: 'Urologie' },
  { value: 'nephrology', label: 'Nefrologie' },
  { value: 'rheumatology', label: 'Reumatologie' },
  { value: 'pediatrics', label: 'Pediatrie' },
  { value: 'psychiatry', label: 'Psihiatrie' },
  { value: 'oncology', label: 'Oncologie' },
  { value: 'hematology', label: 'Hematologie' },
  { value: 'infectious_diseases', label: 'Boli infecțioase' },
  { value: 'surgery', label: 'Chirurgie' },
  { value: 'internal_medicine', label: 'Medicină internă' },
  { value: 'dentistry', label: 'Stomatologie' },
  { value: 'nutrition_supplements', label: 'Nutriție / Suplimente alimentare' },
  { value: 'other', label: 'Alte arii' },
];

const ALL_STANDARD_AREAS = THERAPEUTIC_AREAS.filter((a) => a.value !== 'other').map((a) => a.value);

const COMMUNICATION_CHANNELS = [
  { value: 'email', label: 'E-mail', description: 'Buletine informative, oferte comerciale și materiale educaționale pe email' },
  { value: 'phone', label: 'Telefon (apel vocal)', description: 'Contactare telefonică directă de către reprezentantul medical' },
  { value: 'sms', label: 'SMS', description: 'Alerte rapide, invitații la conferințe și evenimente prin mesaje SMS' },
];

const INITIAL_FORM_DATA = {
  firstName: '',
  lastName: '',
  specialization: '',
  medicalUnit: '',
  phone: '',
  email: '',
  communicationChannels: [],
  therapeuticAreas: [],
  otherTherapeuticArea: '',
  marketingConsent: false,
  refuseMarketing: false,
};

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const cleanPhone = (val) => val.replace(/[\s\-\(\)\.]/g, '');
const PHONE_PATTERN = /^(\+?373|0)?[267]\d{7}$/;

const validateForm = (values) => {
  const nextErrors = {};

  if (!values.lastName.trim()) nextErrors.lastName = 'Introduceți numele.';
  if (!values.firstName.trim()) nextErrors.firstName = 'Introduceți prenumele.';
  if (!values.specialization.trim()) nextErrors.specialization = 'Selectați specializarea.';
  if (!values.medicalUnit.trim()) nextErrors.medicalUnit = 'Introduceți unitatea medicală sau farmacia afiliată.';
  if (!EMAIL_PATTERN.test(values.email.trim())) nextErrors.email = 'Introduceți o adresă de email validă (ex: nume@domeniu.md).';

  // Dacă utilizatorul a bifat explicit refuzul comunicărilor de marketing direct
  if (values.refuseMarketing) {
    if (values.phone && values.phone.trim()) {
      const cleaned = cleanPhone(values.phone.trim());
      if (!PHONE_PATTERN.test(cleaned)) {
        nextErrors.phone = 'Introduceți un număr de telefon valid din Republica Moldova (ex: 069 123 456).';
      }
    }
    return nextErrors;
  }

  const requiresPhone = Array.isArray(values.communicationChannels) && (
    values.communicationChannels.includes('phone') || values.communicationChannels.includes('sms')
  );

  if (requiresPhone && !values.phone.trim()) {
    nextErrors.phone = 'Introduceți numărul de telefon (obligatoriu pentru canalele Telefon sau SMS).';
  } else if (values.phone && values.phone.trim()) {
    const cleaned = cleanPhone(values.phone.trim());
    if (!PHONE_PATTERN.test(cleaned)) {
      nextErrors.phone = 'Introduceți un număr de telefon valid din Republica Moldova (ex: 069 123 456).';
    }
  }

  if (!values.communicationChannels || values.communicationChannels.length === 0) {
    nextErrors.communicationChannels = 'Selectați cel puțin un canal de comunicare (E-mail, Telefon sau SMS) sau bifați opțiunea de refuz.';
  }
  if (values.therapeuticAreas.length === 0) nextErrors.therapeuticAreas = 'Selectați cel puțin o arie terapeutică sau bifați opțiunea de refuz.';
  if (values.therapeuticAreas.includes('other') && !values.otherTherapeuticArea.trim()) {
    nextErrors.otherTherapeuticArea = 'Specificați celelalte arii terapeutice.';
  }
  if (!values.marketingConsent) nextErrors.marketingConsent = 'Confirmați acordul pentru marketing direct sau bifați opțiunea de refuz.';

  return nextErrors;
};

function Field({ id, label, error, helper, required = false, ...inputProps }) {
  const describedBy = [error ? `${id}-error` : '', helper ? `${id}-helper` : ''].filter(Boolean).join(' ') || undefined;

  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-sm font-semibold text-slate-800">
        {label}{required && <span className="ml-1 text-blue-700" aria-hidden="true">*</span>}
      </label>
      <input
        id={id}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
        className={`min-h-11 w-full rounded-lg border bg-white px-3.5 py-2.5 text-base text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-700/25 ${
          error ? 'border-red-600 focus:border-red-600' : 'border-slate-300 focus:border-blue-700'
        }`}
        {...inputProps}
      />
      {helper && <p id={`${id}-helper`} className="mt-1.5 text-xs leading-5 text-slate-600">{helper}</p>}
      {error && <p id={`${id}-error`} role="alert" className="mt-1.5 text-sm font-medium text-red-700">{error}</p>}
    </div>
  );
}

export default function MarketingConsentPage() {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [specialities, setSpecialities] = useState([]);
  const [loadingSpecialities, setLoadingSpecialities] = useState(true);

  // Stare pentru confirmare prin cod din email (OTP)
  const [verificationSession, setVerificationSession] = useState(null);
  const [enteredCode, setEnteredCode] = useState('');
  const [isVerifyingCode, setIsVerifyingCode] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [confirmedDetails, setConfirmedDetails] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Consimțământ pentru marketing direct | DITA ESTFARM';
    return () => { document.title = previousTitle; };
  }, []);

  // Verificare dacă utilizatorul a venit prin link cu token (?token=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      api.get(`/api/marketing-consent/session/${encodeURIComponent(tokenParam)}`)
        .then((res) => {
          setVerificationSession({
            token: tokenParam,
            email: res.data.email,
            name: res.data.name,
            consentId: res.data.consentId,
            specialization: res.data.specialization,
            medicalUnit: res.data.medicalUnit,
          });
          if (res.data.isConfirmed) {
            setIsConfirmed(true);
            setConfirmedDetails({
              consentId: res.data.consentId,
              name: res.data.name,
              confirmedAt: res.data.confirmedAt,
            });
          }
        })
        .catch((err) => {
          console.error('Session error:', err);
        });
    }
  }, []);

  useEffect(() => {
    api.get('/api/marketing-consent/specialities')
      .then((res) => {
        if (Array.isArray(res.data)) {
          setSpecialities(res.data);
        }
      })
      .catch((err) => console.error('Eroare la încărcarea specialităților:', err))
      .finally(() => setLoadingSpecialities(false));
  }, []);

  const errors = useMemo(() => validateForm(formData), [formData]);
  const isFormValid = Object.keys(errors).length === 0;

  const isAllSelected = useMemo(() => {
    return ALL_STANDARD_AREAS.every((area) => formData.therapeuticAreas.includes(area));
  }, [formData.therapeuticAreas]);

  const requiresPhone = useMemo(() => {
    return Array.isArray(formData.communicationChannels) && (
      formData.communicationChannels.includes('phone') || formData.communicationChannels.includes('sms')
    );
  }, [formData.communicationChannels]);

  const checklistItems = useMemo(() => {
    const items = [
      {
        id: 'name',
        label: 'Nume și prenume',
        isCompleted: Boolean(formData.lastName.trim() && formData.firstName.trim()),
      },
      {
        id: 'specialization',
        label: 'Specializare medicală',
        isCompleted: Boolean(formData.specialization.trim()),
      },
      {
        id: 'medicalUnit',
        label: 'Unitate medicală / farmacie afiliată',
        isCompleted: Boolean(formData.medicalUnit.trim()),
      },
      {
        id: 'email',
        label: 'Adresă de email validă',
        isCompleted: Boolean(EMAIL_PATTERN.test(formData.email.trim())),
      },
    ];

    if (formData.refuseMarketing) {
      items.push({
        id: 'refuseMarketing',
        label: 'Opțiune de refuz marketing direct bifată',
        isCompleted: true,
      });
      return items;
    }

    if (requiresPhone) {
      items.push({
        id: 'phone',
        label: 'Număr de telefon valid (necesar pentru Telefon/SMS)',
        isCompleted: Boolean(formData.phone.trim() && PHONE_PATTERN.test(cleanPhone(formData.phone.trim()))),
      });
    }

    items.push(
      {
        id: 'communicationChannels',
        label: 'Canale de comunicare alese (minim 1)',
        isCompleted: formData.communicationChannels.length > 0,
      },
      {
        id: 'therapeuticAreas',
        label: 'Arii terapeutice selectate pentru Marketing direct',
        isCompleted:
          formData.therapeuticAreas.length > 0 &&
          (!formData.therapeuticAreas.includes('other') || Boolean(formData.otherTherapeuticArea.trim())),
      },
      {
        id: 'marketingConsent',
        label: 'Acord pentru marketing direct bifat',
        isCompleted: Boolean(formData.marketingConsent),
      }
    );

    return items;
  }, [formData, requiresPhone]);

  const completedCount = checklistItems.filter((item) => item.isCompleted).length;
  const isAllCompleted = completedCount === checklistItems.length && isFormValid;

  const scrollToPageTop = () => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === 'phone') {
      // Regex: elimină instant orice literă sau caracter non-numeric (permite doar cifre și spații)
      const sanitizedPhone = value.replace(/[^\d\s]/g, '').slice(0, 11);
      setFormData((current) => ({ ...current, phone: sanitizedPhone }));
      return;
    }
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const markTouched = (event) => {
    setTouched((current) => ({ ...current, [event.target.name]: true }));
  };

  const toggleChannel = (channelValue) => {
    setTouched((current) => ({ ...current, communicationChannels: true }));
    setFormData((current) => {
      const exists = current.communicationChannels.includes(channelValue);
      const nextChannels = exists
        ? current.communicationChannels.filter((c) => c !== channelValue)
        : [...current.communicationChannels, channelValue];

      // Req 3: Dacă bifează SMS sau Telefon și nu a introdus telefonul, focalizează și colorează în roșu câmpul
      if (!exists && (channelValue === 'phone' || channelValue === 'sms') && (!current.phone || !current.phone.trim())) {
        setTouched((prevTouched) => ({ ...prevTouched, phone: true }));
        setTimeout(() => {
          const phoneField = document.getElementById('phone');
          if (phoneField) {
            phoneField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            phoneField.focus();
          }
        }, 80);
      }

      return {
        ...current,
        refuseMarketing: false,
        communicationChannels: nextChannels,
      };
    });
  };

  const toggleSelectAll = () => {
    setTouched((current) => ({ ...current, therapeuticAreas: true }));
    setFormData((current) => {
      const allSelected = ALL_STANDARD_AREAS.every((area) => current.therapeuticAreas.includes(area));
      if (allSelected) {
        return {
          ...current,
          refuseMarketing: false,
          therapeuticAreas: current.therapeuticAreas.includes('other') ? ['other'] : [],
        };
      } else {
        const nextAreas = current.therapeuticAreas.includes('other')
          ? [...ALL_STANDARD_AREAS, 'other']
          : [...ALL_STANDARD_AREAS];
        return {
          ...current,
          refuseMarketing: false,
          therapeuticAreas: nextAreas,
        };
      }
    });
  };

  const toggleTherapeuticArea = (area) => {
    setTouched((current) => ({ ...current, therapeuticAreas: true }));
    setFormData((current) => {
      const isSelected = current.therapeuticAreas.includes(area);
      const therapeuticAreas = isSelected
        ? current.therapeuticAreas.filter((value) => value !== area)
        : [...current.therapeuticAreas, area];

      return {
        ...current,
        refuseMarketing: false,
        therapeuticAreas,
        otherTherapeuticArea: area === 'other' && isSelected ? '' : current.otherTherapeuticArea,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched({
      firstName: true,
      lastName: true,
      specialization: true,
      medicalUnit: true,
      phone: true,
      email: true,
      communicationChannels: true,
      therapeuticAreas: true,
      otherTherapeuticArea: true,
      marketingConsent: true,
    });

    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setApiError(null);

    try {
      const res = await api.post('/api/marketing-consent', formData);
      const mappedChannels = formData.refuseMarketing
        ? 'Niciunul (Refuz comunicări promoționale)'
        : (formData.communicationChannels.map(c => {
            const found = COMMUNICATION_CHANNELS.find(ch => ch.value === c);
            return found ? found.label : c;
          }).join(', ') || 'Niciunul');

      const mappedAreas = formData.refuseMarketing
        ? 'Niciuna (Refuz marketing direct)'
        : (formData.therapeuticAreas.map(a => {
            if (a === 'other' && formData.otherTherapeuticArea) return `Alte: ${formData.otherTherapeuticArea}`;
            const found = THERAPEUTIC_AREAS.find(th => th.value === a);
            return found ? found.label : a;
          }).join(', ') || 'Niciuna');

      setVerificationSession({
        token: res.data.token,
        email: res.data.email || formData.email,
        name: res.data.name || `${formData.firstName} ${formData.lastName}`,
        consentId: res.data.consentId,
        specialization: formData.specialization,
        medicalUnit: formData.medicalUnit,
        channels: mappedChannels,
        therapeuticAreas: mappedAreas,
        refuseMarketing: formData.refuseMarketing,
      });
      setEnteredCode('');
      setVerificationError(null);
      scrollToPageTop();
    } catch (err) {
      console.error('Error submitting marketing consent:', err);
      setApiError(translateApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event?.preventDefault();
    const cleanCode = enteredCode.trim().replace(/\D/g, '');
    if (!verificationSession?.token || cleanCode.length !== 6) {
      setVerificationError('Introduceți codul complet din 6 cifre primit pe email.');
      return;
    }

    setIsVerifyingCode(true);
    setVerificationError(null);

    try {
      const res = await api.post('/api/marketing-consent/verify-code', {
        token: verificationSession.token,
        code: cleanCode,
      });

      setIsConfirmed(true);
      setConfirmedDetails({
        consentId: res.data.consentId || verificationSession.consentId,
        name: res.data.name || verificationSession.name,
        specialization: res.data.specialization || verificationSession.specialization,
        medicalUnit: res.data.medicalUnit || verificationSession.medicalUnit,
        channels: res.data.channels || verificationSession.channels,
        therapeuticAreas: res.data.therapeuticAreas || verificationSession.therapeuticAreas,
        confirmedAt: res.data.confirmedAt,
        device: res.data.device,
      });
      scrollToPageTop();
    } catch (err) {
      console.error('Error verifying code:', err);
      setVerificationError(translateApiError(err));
    } finally {
      setIsVerifyingCode(false);
    }
  };

  const resetVerificationState = () => {
    setVerificationSession(null);
    setEnteredCode('');
    setVerificationError(null);
    setIsConfirmed(false);
    setConfirmedDetails(null);
    window.history.replaceState({}, document.title, window.location.pathname);
    scrollToPageTop();
  };

  // 1. Ecranul final de confirmare cu succes (Layout lărgit, text optimizat cu -10% și notă aranjată)
  if (isConfirmed) {
    const details = confirmedDetails || verificationSession || {};
    return (
      <main className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-emerald-50/40 via-slate-50 to-slate-100 px-4 py-8 sm:py-12 text-slate-900">
        <section className="mx-auto max-w-[760px] rounded-3xl bg-white p-6 sm:p-10 text-center shadow-xl border border-emerald-100 border-t-8 border-t-emerald-500" aria-labelledby="confirmed-title">
          {/* Cerc mare verde cu bifa */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr from-emerald-600 to-emerald-400 text-white shadow-lg shadow-emerald-500/25">
            <Check size={36} className="stroke-[3]" aria-hidden="true" />
          </div>

          {/* Badge companie */}
          <div className="mt-5 inline-block rounded-full bg-emerald-50 px-3.5 py-1 text-[11.5px] font-bold tracking-wider uppercase text-emerald-700 border border-emerald-200/80">
            S.R.L. DITA ESTFARM
          </div>

          <h1 id="confirmed-title" className="mt-3.5 text-2xl sm:text-[26px] font-extrabold text-slate-950 tracking-tight">
            Consimțământ Confirmat cu Succes!
          </h1>

          <p className="mx-auto mt-2 max-w-lg text-[13px] leading-5 text-slate-600">
            Vă mulțumim, <strong className="text-slate-900">{details.name}</strong>! Opțiunile dumneavoastră au fost înregistrate și validate oficial.
          </p>

          {/* Caseta cu detaliile complete ale medicului */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-6 text-left text-[13px] leading-5 text-slate-800">
            <div className="divide-y divide-dashed divide-slate-200">
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                <span className="text-slate-500 font-medium text-[12.5px]">Medic / Specialist:</span>
                <span className="font-bold text-slate-900 sm:text-right">{details.name}</span>
              </div>
              {details.specialization && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px]">Specializare:</span>
                  <span className="font-semibold text-slate-900 sm:text-right">{details.specialization}</span>
                </div>
              )}
              {details.medicalUnit && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px]">Unitate Medicală / Farmacie:</span>
                  <span className="font-semibold text-slate-900 sm:text-right">{details.medicalUnit}</span>
                </div>
              )}
              {details.channels && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px]">Canale de comunicare aprobate:</span>
                  <span className="font-bold text-blue-700 sm:text-right">{details.channels}</span>
                </div>
              )}
              {details.therapeuticAreas && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px] shrink-0">Arii terapeutice selectate pentru Marketing direct:</span>
                  <span className="font-bold text-slate-900 sm:text-right max-w-md">{details.therapeuticAreas}</span>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                <span className="text-slate-500 font-medium text-[12.5px]">Stare:</span>
                <span className="font-bold text-emerald-700 sm:text-right">✓ Confirmat prin cod securizat (OTP)</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                <span className="text-slate-500 font-medium text-[12.5px]">Număr Referință:</span>
                <span className="font-mono font-bold text-slate-900 sm:text-right">DITA-CONS-{details.consentId}</span>
              </div>
              {details.confirmedAt && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px]">Data confirmării:</span>
                  <span className="font-semibold text-slate-900 sm:text-right">{new Date(details.confirmedAt).toLocaleString('ro-RO')}</span>
                </div>
              )}
              {details.device && (
                <div className="flex flex-col sm:flex-row sm:justify-between py-2 gap-1">
                  <span className="text-slate-500 font-medium text-[12.5px]">Dispozitiv:</span>
                  <span className="font-semibold text-slate-900 sm:text-right">{details.device}</span>
                </div>
              )}
            </div>
          </div>

          {/* Caseta Notă Legală și Retragere Consimțământ (aranjată îngrijit și structurat) */}
          <div className="mt-6 rounded-2xl border border-slate-200/90 bg-slate-50/70 p-4 sm:p-5 text-left text-[12px] leading-relaxed text-slate-600 space-y-2.5">
            <div className="flex items-start gap-2.5">
              <span className="text-emerald-600 font-bold text-sm mt-0.5 shrink-0">✓</span>
              <p className="flex-1">
                Acordul dumneavoastră privind comunicările de marketing direct a fost validat și înregistrat în baza de date <strong>S.R.L. DITA ESTFARM</strong> conform{' '}
                <a
                  href="https://www.legis.md/cautare/getResults?doc_id=144671&lang=ro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-900"
                >
                  Legii Republicii Moldova nr. 195 din 25.07.2024 privind protecția datelor cu caracter personal
                </a>.
              </p>
            </div>

            <div className="flex items-start gap-2.5 pt-2.5 border-t border-slate-200/70">
              <span className="text-blue-600 font-bold text-sm mt-0.5 shrink-0">✉</span>
              <div className="flex-1 space-y-2">
                <p>
                  Acest consimțământ este separat și opțional – refuzul nu afectează în niciun fel relația profesională/comercială existentă cu DITA ESTFARM și prelucrarea datelor mele profesionale în scopul organizării vizitelor reprezentanților medicali în temeiul interesului legitim.
                </p>
                <p>
                  Consimțământul poate fi retras sau modificat oricând, integral ori parțial, pentru una sau mai multe arii terapeutice sau canale de comunicare, fără prezentarea unor justificări, prin linkul de dezabonare/gestionare a preferințelor din e-mailurile promoționale, accesând linkul{' '}
                  <a href="/marketing-consent/unsubscribe" className="font-bold text-blue-700 underline">https://crm.dita.md/marketing-consent/unsubscribe</a>{' '}
                  (se introduce email-ul, după care pe email vine codul de confirmare, se introduce codul și se apasă dezabonare), prin e-mail la{' '}
                  <a href="mailto:dpo@dita.md" className="font-bold text-blue-700 underline">dpo@dita.md</a>{' '}
                  sau prin reprezentantul medical (în formă scrisă). Comunicările de marketing încetează în termen de 30 zile de la înregistrarea retragerii.
                </p>
                <p>
                  Retragerea consimțământului nu afectează legalitatea prelucrării datelor efectuate anterior retragerii și nici prelucrările efectuate în baza unui temei juridic distinct.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-4 text-center text-[12px] font-medium text-slate-400">
            Vă mulțumim! Puteți închide această fereastră în siguranță.
          </p>

          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={resetVerificationState}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-100 px-5 py-2 text-[13px] font-bold text-slate-700 hover:bg-slate-200 transition-colors"
            >
              <ChevronLeft size={15} aria-hidden="true" /> Formular nou
            </button>
          </div>
        </section>
      </main>
    );
  }

  // 2. Ecranul de introducere a codului de confirmare din email (OTP)
  if (verificationSession) {
    return (
      <main className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 px-4 py-10 text-slate-900 sm:py-16">
        <section className="mx-auto max-w-lg rounded-2xl bg-white px-6 py-10 shadow-sm border border-slate-200 sm:px-10 sm:py-12" aria-labelledby="otp-title">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700">
            <LockKeyhole size={28} aria-hidden="true" />
          </div>

          <h1 id="otp-title" className="mt-5 text-center text-2xl font-bold text-slate-950 sm:text-3xl">
            Confirmare Consimțământ
          </h1>

          <p className="mt-2.5 text-center text-sm leading-6 text-slate-600">
            Am transmis un cod de confirmare din 6 cifre pe adresa de email:
          </p>
          <p className="mt-1 text-center font-bold text-blue-800 break-all text-base">
            {verificationSession.email}
          </p>

          <form onSubmit={handleVerifyCode} className="mt-7">
            <label htmlFor="otp-code-input" className="block text-center text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">
              Introduceți codul din 6 cifre
            </label>

            <div className="flex justify-center">
              <input
                id="otp-code-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={enteredCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setEnteredCode(val);
                  if (verificationError) setVerificationError(null);
                }}
                placeholder="••••••"
                className="h-16 w-full max-w-[280px] rounded-xl border-2 border-slate-300 text-center font-mono text-3xl font-extrabold tracking-[0.4em] text-slate-900 shadow-xs transition-all focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-100 placeholder:tracking-[0.3em] placeholder:text-slate-300"
                autoFocus
              />
            </div>

            {verificationError && (
              <div role="alert" className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-red-50 p-3 text-xs font-semibold text-red-700">
                <CircleAlert size={16} className="shrink-0" />
                <span>{verificationError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifyingCode || enteredCode.trim().length !== 6}
              className="mt-6 flex w-full min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-3 text-base font-bold text-white shadow-sm transition-all hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-blue-200"
            >
              {isVerifyingCode ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Se verifică codul...</span>
                </>
              ) : (
                <>
                  <Check size={18} />
                  <span>Confirmă</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-7 border-t border-slate-100 pt-5 text-center">
            <p className="text-xs text-slate-500 leading-5">
              Nu ați primit codul? Vă rugăm să verificați și dosarul de <strong>Spam / Junk</strong> sau reveniți la formular dacă ați introdus greșit emailul.
            </p>

            <button
              type="button"
              onClick={resetVerificationState}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-blue-700"
            >
              <ChevronLeft size={14} /> Înapoi la formular
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 px-4 py-10 text-slate-900 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-[1080px]">
        <header className="mb-8 sm:mb-10">
          <img src={ditaLogo} alt="DITA ESTFARM" className="h-auto w-36" />
          <p className="mt-7 text-sm font-semibold text-blue-800">S.R.L. DITA ESTFARM</p>
          <h1 className="mt-2 max-w-2xl text-3xl font-bold tracking-[-0.02em] text-slate-950 sm:text-4xl sm:leading-tight">
            Consimțământ pentru comunicări de marketing direct
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-700">
            Prin completarea acestui formular, puteți alege dacă doriți să primiți comunicări de marketing direct din partea S.R.L. DITA ESTFARM și puteți selecta ariile terapeutice pentru care doriți să primiți aceste comunicări.
          </p>
          <div className="mt-6 flex gap-3 rounded-xl bg-blue-50 p-4 text-sm leading-6 text-blue-950">
            <Info className="mt-0.5 shrink-0 text-blue-700" size={19} aria-hidden="true" />
            <p><strong>Consimțământul este separat și opțional.</strong> Refuzul acordării consimțământului nu afectează relația profesională sau comercială existentă cu DITA ESTFARM.</p>
          </div>
        </header>

        <form onSubmit={handleSubmit} noValidate className="rounded-2xl bg-white px-5 py-7 shadow-sm sm:px-10 sm:py-10">
          <section aria-labelledby="identity-title">
            <h2 id="identity-title" className="text-xl font-bold text-slate-950">Date de identificare</h2>
            <p className="mt-1 text-sm text-slate-600">Câmpurile marcate cu * sunt obligatorii.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <Field id="lastName" name="lastName" label="Nume" type="text" autoComplete="family-name" required value={formData.lastName} onChange={updateField} onBlur={markTouched} error={touched.lastName ? errors.lastName : undefined} />
              <Field id="firstName" name="firstName" label="Prenume" type="text" autoComplete="given-name" required value={formData.firstName} onChange={updateField} onBlur={markTouched} error={touched.firstName ? errors.firstName : undefined} />
              <div>
                <label htmlFor="specialization" className="mb-2 block text-sm font-semibold text-slate-800">
                  Specializare <span className="ml-1 text-blue-700" aria-hidden="true">*</span>
                </label>
                <select
                  id="specialization"
                  name="specialization"
                  required
                  value={formData.specialization}
                  onChange={updateField}
                  onBlur={markTouched}
                  aria-invalid={Boolean(touched.specialization && errors.specialization)}
                  aria-describedby={touched.specialization && errors.specialization ? 'specialization-error' : undefined}
                  className={`min-h-11 w-full rounded-lg border bg-white px-3.5 py-2.5 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-700/25 ${
                    touched.specialization && errors.specialization ? 'border-red-600 focus:border-red-600' : 'border-slate-300 focus:border-blue-700'
                  }`}
                >
                  <option value="">
                    {loadingSpecialities ? 'Se încarcă specialitățile...' : 'Selectați specializarea...'}
                  </option>
                  {specialities.map((spec) => (
                    <option key={spec.speciality_id} value={spec.description}>
                      {spec.description}
                    </option>
                  ))}
                </select>
                {touched.specialization && errors.specialization && (
                  <p id="specialization-error" role="alert" className="mt-1.5 text-sm font-medium text-red-700">
                    {errors.specialization}
                  </p>
                )}
              </div>
              <Field id="medicalUnit" name="medicalUnit" label="Unitate medicală / farmacie afiliată" type="text" autoComplete="organization" required value={formData.medicalUnit} onChange={updateField} onBlur={markTouched} error={touched.medicalUnit ? errors.medicalUnit : undefined} />
              <div>
                <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-slate-800">
                  Telefon de contact
                  {requiresPhone ? (
                    <span className="ml-1 text-blue-700" title="Obligatoriu deoarece ați selectat canalul Telefon sau SMS">*</span>
                  ) : (
                    <span className="ml-1.5 text-xs font-normal text-slate-500">(opțional)</span>
                  )}
                </label>
                <div
                  className={`flex items-center rounded-lg border bg-white transition-colors focus-within:ring-2 focus-within:ring-blue-700/25 ${
                    touched.phone && errors.phone ? 'border-red-600 focus-within:border-red-600' : 'border-slate-300 focus-within:border-blue-700'
                  }`}
                >
                  <div className="flex shrink-0 items-center gap-2 border-r border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700 select-none">
                    <img
                      src={flagMd}
                      alt="MD"
                      className="h-3.5 w-5 rounded-[2px] object-cover shadow-xs border border-slate-300"
                    />
                    <span className="text-xs font-bold text-slate-700">+373</span>
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9\s]*"
                    autoComplete="tel"
                    placeholder="69 123 456"
                    value={formData.phone}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Backspace' ||
                        e.key === 'Tab' ||
                        e.key === 'Delete' ||
                        e.key === 'ArrowLeft' ||
                        e.key === 'ArrowRight' ||
                        e.key === 'ArrowUp' ||
                        e.key === 'ArrowDown' ||
                        e.key === 'Enter' ||
                        e.key === 'Home' ||
                        e.key === 'End' ||
                        e.ctrlKey ||
                        e.metaKey
                      ) {
                        return;
                      }
                      if (!/[\d\s]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={updateField}
                    onBlur={markTouched}
                    aria-invalid={Boolean(touched.phone && errors.phone)}
                    aria-describedby={touched.phone && errors.phone ? 'phone-error' : undefined}
                    className="min-h-11 w-full rounded-r-lg bg-transparent px-3.5 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
                  />
                </div>
                {requiresPhone && !formData.phone.trim() && !errors.phone && (
                  <p className="mt-1 text-xs text-blue-700 font-medium">
                    Necesar pentru canalul de comunicare {formData.communicationChannels.includes('phone') && formData.communicationChannels.includes('sms') ? 'Telefon și SMS' : formData.communicationChannels.includes('phone') ? 'Telefon' : 'SMS'} selectat.
                  </p>
                )}
                {touched.phone && errors.phone && (
                  <p id="phone-error" role="alert" className="mt-1.5 text-sm font-medium text-red-700">
                    {errors.phone}
                  </p>
                )}
              </div>
              <Field id="email" name="email" label="Adresă de email" type="email" autoComplete="email" required value={formData.email} onChange={updateField} onBlur={markTouched} error={touched.email ? errors.email : undefined} helper="Pe această adresă veți primi codul de confirmare a consimțământului." />
            </div>
          </section>

          <section aria-labelledby="crm-info-title" className="mt-9 rounded-xl bg-slate-50 p-5 sm:p-6 border border-slate-200/80">
            <h2 id="crm-info-title" className="text-lg font-bold text-slate-950">
              Informare privind prelucrarea datelor profesionale în CRM
            </h2>
            <div className="mt-3 space-y-3 text-sm leading-6 text-slate-700">
              <p>
                Informațiile complete privind prelucrarea datelor dumneavoastră cu caracter personal, inclusiv destinatarii sau categoriile de destinatari, perioada de păstrare, drepturile persoanei vizate și dreptul de a depune plângere la Centrul Național pentru Protecția Datelor cu Caracter Personal sunt disponibile în Nota completă de informare privind prelucrarea datelor profesionale și marketingul direct, accesibilă la{' '}
                <a
                  href="/marketing-consent/nota-de-informare"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
                >
                  https://crm.dita.md/marketing-consent/nota-de-informare
                </a>.
              </p>
              <p>
                S.R.L. DITA ESTFARM prelucrează datele mele de contact profesionale în sistemul intern de gestiune a vizitelor (CRM), exclusiv pentru organizarea, planificarea și monitorizarea vizitelor reprezentanților / reprezentanților medicali și gestionarea relației profesionale/comerciale cu mine (inclusiv farmacovigilență și comunicări operaționale).
              </p>
              <p>
                Această prelucrare se face în temeiul interesului legitim al operatorului, în condițiile Legii nr. 195/2024 privind protecția datelor cu caracter personal.
              </p>
              <p className="font-semibold text-slate-900">
                Această prelucrare este distinctă de consimțământul pentru marketing direct.
              </p>
              <p>
                Am fost informat că pot să mă opun în orice moment acestei prelucrări realizate în temeiul interesului legitim, în condițiile art. 21 din Legea nr. 195/2024, prin transmiterea unei solicitări la{' '}
                <a href="mailto:dpo@dita.md" className="font-semibold text-blue-800 underline decoration-blue-300 underline-offset-2">
                  dpo@dita.md
                </a>{' '}
                sau prin informarea directă a reprezentantului medical.
              </p>
            </div>
          </section>

          <section aria-labelledby="marketing-title" className="mt-9 border-t border-slate-200 pt-9">
            <h2 id="marketing-title" className="text-xl font-bold text-slate-950">Consimțământ pentru marketing direct</h2>
            <div className="mt-4 flex items-start gap-3 rounded-xl bg-emerald-50 p-4 text-emerald-950" role="note">
              <CircleAlert className="mt-0.5 shrink-0 text-emerald-700" size={21} strokeWidth={2.25} aria-hidden="true" />
              <p className="text-sm font-medium leading-6">Sunt de acord ca S.R.L. DITA ESTFARM să îmi prelucreze datele de contact (nume, prenume, telefon și email) în scop de marketing direct — informări despre produse noi, oferte comerciale și invitații la evenimente promoționale — în legătură cu ariile terapeutice selectate mai jos.</p>
            </div>

            <fieldset className="mt-7" aria-describedby={touched.therapeuticAreas && errors.therapeuticAreas ? 'therapeuticAreas-error therapeuticAreas-note' : 'therapeuticAreas-note'}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <legend className="text-base font-bold leading-6 text-slate-950">Selectați ariile terapeutice pentru care doriți să primiți comunicări</legend>
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {/* Lista ariilor terapeutice */}
                {THERAPEUTIC_AREAS.map((area) => {
                  const isChecked = formData.therapeuticAreas.includes(area.value);
                  return (
                    <label
                      key={area.value}
                      className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3.5 py-2.5 transition-colors border ${
                        isChecked
                          ? 'bg-blue-50 text-blue-950 border-blue-200 font-medium'
                          : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="therapeuticAreas"
                        value={area.value}
                        checked={isChecked}
                        onChange={() => toggleTherapeuticArea(area.value)}
                        className="h-5 w-5 rounded border-slate-400 text-blue-700 focus:ring-blue-700"
                      />
                      <span className="text-sm font-medium leading-5">{area.label}</span>
                    </label>
                  );
                })}
              </div>

              {formData.therapeuticAreas.includes('other') && (
                <div className="mt-4">
                  <Field
                    id="otherTherapeuticArea"
                    name="otherTherapeuticArea"
                    label="Specificați alte arii terapeutice"
                    type="text"
                    required
                    value={formData.otherTherapeuticArea}
                    onChange={updateField}
                    onBlur={markTouched}
                    error={touched.otherTherapeuticArea ? errors.otherTherapeuticArea : undefined}
                  />
                </div>
              )}

              {/* Opțiunea SELECTARE TOTALĂ la finalul listei, integrată natural fără linie despărțitoare */}
              <div className="mt-3">
                <label
                  className={`flex min-h-12 cursor-pointer items-center justify-between rounded-xl px-4 py-3 transition-all border ${
                    isAllSelected
                      ? 'bg-blue-700 text-white border-blue-700 shadow-sm'
                      : 'bg-blue-50/80 text-blue-950 border-blue-200 hover:bg-blue-100/80'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="selectAllTherapeuticAreas"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="h-5 w-5 rounded border-blue-400 text-blue-700 focus:ring-blue-700"
                    />
                    <div>
                      <span className="text-sm font-bold tracking-wide">
                        SELECTARE TOTALĂ
                      </span>
                      <p className={`text-xs mt-0.5 ${isAllSelected ? 'text-blue-100' : 'text-slate-600'}`}>
                        Bifează sau debifează toate ariile terapeutice de mai sus
                      </p>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${isAllSelected ? 'bg-white/20 text-white' : 'bg-blue-100 text-blue-800'}`}>
                    {isAllSelected ? 'Toate selectate' : 'Selectează tot'}
                  </span>
                </label>
              </div>

              {touched.therapeuticAreas && errors.therapeuticAreas && (
                <p id="therapeuticAreas-error" role="alert" className="mt-2 text-sm font-medium text-red-700">
                  {errors.therapeuticAreas}
                </p>
              )}
            </fieldset>

            <p id="therapeuticAreas-note" className="mt-4 text-sm leading-6 text-slate-600">
              Pentru ariile terapeutice nebifate, nu doresc să primesc comunicări de marketing direct.
            </p>
          </section>

          <section aria-labelledby="channels-title" className="mt-9 border-t border-slate-200 pt-9">
            <h2 id="channels-title" className="text-xl font-bold text-slate-950">
              Canale de comunicare preferate <span className="ml-1 text-blue-700" aria-hidden="true">*</span>
            </h2>
            <p className="mt-1.5 text-sm text-slate-600">
              Selectați prin ce mijloace acceptați să primiți comunicările și informările din partea reprezentanților noștri (alegeți cel puțin un canal):
            </p>

            <fieldset className="mt-4" aria-describedby={touched.communicationChannels && errors.communicationChannels ? 'communicationChannels-error' : undefined}>
              <legend className="sr-only">Canale de comunicare</legend>
              <div className="grid gap-3 sm:grid-cols-3">
                {COMMUNICATION_CHANNELS.map((ch) => {
                  const isChecked = formData.communicationChannels.includes(ch.value);
                  return (
                    <label
                      key={ch.value}
                      className={`flex cursor-pointer flex-col justify-between rounded-xl border p-4 transition-all ${
                        isChecked
                          ? 'border-blue-700 bg-blue-50/80 shadow-xs ring-1 ring-blue-700/20'
                          : 'border-slate-200 bg-slate-50/60 hover:bg-slate-100/80 text-slate-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          name="communicationChannels"
                          value={ch.value}
                          checked={isChecked}
                          onChange={() => toggleChannel(ch.value)}
                          className="h-5 w-5 rounded border-slate-400 text-blue-700 focus:ring-blue-700"
                        />
                        <span className="text-sm font-bold text-slate-900">{ch.label}</span>
                      </div>
                      <p className="mt-2 text-xs leading-5 text-slate-600">
                        {ch.description}
                      </p>
                    </label>
                  );
                })}
              </div>
              {/* Caseta de refuz marketing direct [Referinta 1] */}
              <div className="mt-4 pt-3 border-t border-slate-200/80">
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl p-3.5 border transition-all ${
                  formData.refuseMarketing
                    ? 'bg-red-50/80 border-red-300 text-red-950 shadow-xs'
                    : 'bg-slate-50 border-slate-200 text-slate-800 hover:bg-slate-100/80'
                }`}>
                  <input
                    type="checkbox"
                    id="refuseMarketing"
                    name="refuseMarketing"
                    checked={formData.refuseMarketing}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        refuseMarketing: checked,
                        communicationChannels: checked ? [] : prev.communicationChannels,
                        therapeuticAreas: checked ? [] : prev.therapeuticAreas,
                        otherTherapeuticArea: checked ? '' : prev.otherTherapeuticArea,
                        marketingConsent: checked ? false : prev.marketingConsent,
                      }));
                      setTouched((prev) => ({ ...prev, refuseMarketing: true, communicationChannels: true }));
                    }}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-400 text-red-600 focus:ring-red-500"
                  />
                  <div>
                    <span className="text-sm font-bold text-slate-900 block">
                      Nu doresc să primesc comunicări de marketing direct.
                    </span>
                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                      Bifând această opțiune, refuzați comunicările promoționale. Datele dumneavoastră profesionale vor fi prelucrate exclusiv în CRM conform interesului legitim pentru organizarea vizitelor.
                    </p>
                  </div>
                </label>
              </div>
              {touched.communicationChannels && errors.communicationChannels && !formData.refuseMarketing && (
                <p id="communicationChannels-error" role="alert" className="mt-2 text-sm font-medium text-red-700">
                  {errors.communicationChannels}
                </p>
              )}
            </fieldset>
          </section>

          <section aria-labelledby="agreement-title" className="mt-9 border-t border-slate-200 pt-9">
            <h2 id="agreement-title" className="sr-only">Confirmarea acordului</h2>
            {formData.refuseMarketing ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950 text-sm">
                <p className="font-bold">Ați optat pentru refuzul comunicărilor de marketing direct.</p>
                <p className="mt-1 text-xs text-amber-800">
                  Nu este necesar să bifați acordul de mai jos. Trimiterea formularului va înregistra opțiunea dumneavoastră de refuz.
                </p>
              </div>
            ) : (
              <>
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl p-4 border transition-colors ${touched.marketingConsent && errors.marketingConsent ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-100'}`}>
                  <input
                    type="checkbox"
                    id="marketingConsent"
                    name="marketingConsent"
                    checked={formData.marketingConsent}
                    onChange={(event) => {
                      updateField(event);
                      setTouched((current) => ({ ...current, marketingConsent: true }));
                    }}
                    aria-invalid={Boolean(touched.marketingConsent && errors.marketingConsent)}
                    aria-describedby={touched.marketingConsent && errors.marketingConsent ? 'marketingConsent-error marketingConsent-note' : 'marketingConsent-note'}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-400 text-blue-700 focus:ring-blue-700"
                  />
                  <span className="text-sm font-semibold leading-6 text-slate-900">
                    Confirm că am citit informațiile de mai sus și sunt de acord cu prelucrarea datelor mele de contact în scop de marketing direct pentru ariile terapeutice selectate, prin canalele de comunicare indicate.
                  </span>
                </label>
                {touched.marketingConsent && errors.marketingConsent && (
                  <p id="marketingConsent-error" role="alert" className="mt-2 text-sm font-medium text-red-700">
                    {errors.marketingConsent}
                  </p>
                )}
              </>
            )}

            <div id="marketingConsent-note" className="mt-5 space-y-3 text-sm leading-6 text-slate-700">
              <p>
                <strong>Acest consimțământ este separat și opțional</strong> – refuzul nu afectează în niciun fel relația profesională/comercială existentă cu DITA ESTFARM și prelucrarea datelor mele profesionale în scopul organizării vizitelor reprezentanților medicali în temeiul interesului legitim.
              </p>
              <p>
                Consimțământul poate fi retras sau modificat oricând, integral ori parțial, pentru una sau mai multe arii terapeutice sau canale de comunicare, fără prezentarea unor justificări, prin linkul de dezabonare/gestionare a preferințelor din e-mailurile promoționale, accesând linkul{' '}
                <a
                  href="/marketing-consent/unsubscribe"
                  className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
                >
                  https://crm.dita.md/marketing-consent/unsubscribe
                </a>{' '}
                (se introduce email-ul, după care pe email vine codul de confirmare, se introduce codul și se apasă dezabonare), prin e-mail la{' '}
                <a
                  href="mailto:dpo@dita.md"
                  className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
                >
                  dpo@dita.md
                </a>{' '}
                sau prin reprezentantul medical (în formă scrisă). Comunicările de marketing încetează în termen de 30 zile de la înregistrarea retragerii.
              </p>
              <p>
                Retragerea consimțământului nu afectează legalitatea prelucrării datelor efectuate anterior retragerii și nici prelucrările efectuate în baza unui temei juridic distinct.
              </p>
              <p className="pt-2 text-xs leading-5 text-slate-500 border-t border-slate-200/70">
                Prelucrarea datelor se realizează în conformitate cu prevederile{' '}
                <a
                  href="https://www.legis.md/cautare/getResults?doc_id=144671&lang=ro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:text-blue-900"
                >
                  Legii Republicii Moldova nr. 195 din 25.07.2024 privind protecția datelor cu caracter personal
                </a>.
              </p>
            </div>
          </section>

          <section aria-labelledby="email-confirmation-title" className="mt-7 flex gap-3 rounded-xl bg-slate-50 p-4 sm:p-5">
            <LockKeyhole className="mt-0.5 shrink-0 text-blue-700" size={20} aria-hidden="true" />
            <div>
              <h2 id="email-confirmation-title" className="text-sm font-bold text-slate-950">Confirmarea ulterioară prin email</h2>
              <p className="mt-1 text-sm leading-6 text-slate-700">
                După trimiterea formularului, veți primi pe adresa de email specificată un cod de confirmare. Înregistrarea va fi considerată confirmată după introducerea codului primit prin email.
              </p>
            </div>
          </section>

          {apiError && (
            <div className="mt-6 rounded-xl bg-red-50 p-4 text-sm font-medium text-red-700">
              {apiError}
            </div>
          )}

          {/* Cerințe pentru finalizare (Checklist dinamic) */}
          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/80 p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Cerințe pentru finalizare
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                  isAllCompleted
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-slate-200 text-slate-700'
                }`}
              >
                {isAllCompleted ? '✓ Toate cerințele sunt îndeplinite' : `${completedCount} din ${checklistItems.length} completate`}
              </span>
            </div>

            {/* Segmente bară de progres */}
            <div
              className="mt-3 grid gap-1.5"
              style={{ gridTemplateColumns: `repeat(${checklistItems.length}, minmax(0, 1fr))` }}
            >
              {checklistItems.map((item) => (
                <div
                  key={item.id}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    item.isCompleted ? 'bg-emerald-500' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>

            {/* Lista cerințelor cu bifă */}
            <div className="mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {checklistItems.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                    item.isCompleted ? 'text-emerald-800' : 'text-slate-500'
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold transition-all ${
                      item.isCompleted
                        ? 'bg-emerald-500 text-white shadow-sm'
                        : 'border border-slate-300 bg-white text-slate-400'
                    }`}
                  >
                    {item.isCompleted ? '✓' : '•'}
                  </span>
                  <span className={item.isCompleted ? 'font-semibold text-slate-900' : 'text-slate-600'}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col items-stretch gap-3 sm:items-end">
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg px-6 py-3 text-base font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 sm:w-auto ${
                formData.refuseMarketing
                  ? 'bg-red-700 hover:bg-red-800 focus:ring-red-700'
                  : 'bg-blue-700 hover:bg-blue-800 focus:ring-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  Se trimite... <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                </>
              ) : formData.refuseMarketing ? (
                <>
                  Trimite înregistrarea (Refuz marketing) <Check size={18} aria-hidden="true" />
                </>
              ) : (
                <>
                  Trimite pentru confirmare <Check size={18} aria-hidden="true" />
                </>
              )}
            </button>
            {!isFormValid && (
              <p className="text-xs leading-5 text-slate-600">
                Completați toate cerințele menționate mai sus pentru a putea trimite formularul.
              </p>
            )}
          </div>
        </form>

        <footer className="mt-7 text-center text-xs leading-5 text-slate-600">
          © {new Date().getFullYear()} S.R.L. DITA ESTFARM. Toate drepturile rezervate.
        </footer>
      </div>
    </main>
  );
}
